/**
 * EmailPasswordSection Component
 *
 * Section that wraps email change and password change forms
 * with icon-badged subsection headers separated by a divider.
 *
 * @module pages/SettingsPage/components/EmailPasswordSection
 */

import { type ReactElement, memo } from 'react';
import { Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Lock, Mail } from 'lucide-react';

import { useGeoMarkupStore } from '@/store';

import { EmailChangeForm } from './EmailChangeForm';
import { PasswordChangeForm } from './PasswordChangeForm';
import styles from './EmailPasswordSection.module.css';

const ICON_SIZE = 18;

interface EmailPasswordSectionProps {
  currentEmail: string;
}

export const EmailPasswordSection = memo(function EmailPasswordSection({
  currentEmail,
}: EmailPasswordSectionProps): ReactElement {
  const { t } = useTranslation();

  const updateUserEmail = useGeoMarkupStore(
    (state) => state.auth.updateUserEmail
  );

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {t('settings.account.sectionTitle')}
      </h2>
      <hr className={styles.divider} />

      <div className={styles.subsectionHeader}>
        <div className={styles.iconBadge}>
          <Mail size={ICON_SIZE} />
        </div>
        <div className={styles.subsectionText}>
          <h3 className={styles.subsectionTitle}>
            {t('settings.account.email.title')}
          </h3>
          <p className={styles.subsectionSubtitle}>
            {t('settings.account.email.subtitle', { email: currentEmail })}
          </p>
        </div>
      </div>
      <EmailChangeForm updateUserEmail={updateUserEmail} />

      <Divider my="lg" />

      <div className={styles.subsectionHeader}>
        <div className={styles.iconBadge}>
          <Lock size={ICON_SIZE} />
        </div>
        <div className={styles.subsectionText}>
          <h3 className={styles.subsectionTitle}>
            {t('settings.account.password.title')}
          </h3>
          <p className={styles.subsectionSubtitle}>
            {t('settings.account.password.subtitle')}
          </p>
        </div>
      </div>
      <PasswordChangeForm />
    </div>
  );
});
